import { useState } from 'react';

import PropTypes from 'prop-types';
import { BsGraphUp } from 'react-icons/bs';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import SectionCard from '#globalComponents/Card/SectionCard';
import Tabs from '#globalComponents/Tabs/Tabs';
import { colors } from '#styles/colors';

import { formatEurCompact, formatNumber } from '../../utils/formatters';

const METRICS = [
	{ id: 'revenue', label: 'Umsatz', format: formatEurCompact },
	{ id: 'earnings', label: 'Gewinn', format: formatEurCompact },
	{ id: 'balanceTotal', label: 'Bilanzsumme', format: formatEurCompact },
	{ id: 'employeeCount', label: 'Mitarbeiter', format: formatNumber },
];

const CHART_HEIGHT = 280;
const LINE_COLOR = colors.primary[500];
const AXIS_TICK = { fontSize: 11, fill: colors.bgBase[800] };
const AXIS_LINE = { stroke: colors.bgBase[500] };

/**
 * Splits one metric into two series so confirmed values render solid and estimates dashed. The last
 * confirmed point before the first estimate is repeated in the estimated series to bridge the lines.
 *
 * @param {Array<Object>} financials - Yearly rows, ascending by year
 * @param {string} metricKey
 * @returns {Array<{year: number, confirmed: number|null, estimated: number|null, isEstimate: boolean}>}
 */
const buildSplitData = (financials, metricKey) => {
	const estimateKey = `${metricKey}IsEstimate`;
	const filtered = financials.filter((f) => f[metricKey] !== null && f[metricKey] !== undefined);

	let lastConfirmedIdx = -1;
	filtered.forEach((f, i) => {
		if (!f[estimateKey]) lastConfirmedIdx = i;
	});

	return filtered.map((f, i) => {
		const isEstimate = Boolean(f[estimateKey]);
		const isBridge = !isEstimate && i === lastConfirmedIdx && filtered.slice(i + 1).some((ff) => ff[estimateKey]);

		return {
			year: f.year,
			confirmed: isEstimate ? null : f[metricKey],
			estimated: isEstimate || isBridge ? f[metricKey] : null,
			isEstimate,
		};
	});
};

const ChartTooltip = ({ active, payload, label, metric, points }) => {
	const entry = active && payload?.find((p) => p.value !== null && p.value !== undefined);
	if (!entry) return null;
	const isEstimate = points.find((d) => d.year === label)?.isEstimate;

	return (
		<div className="rounded-card border border-bgBase-500 bg-white px-3 py-2 text-sm shadow-elevated">
			<p className="mb-0.5 font-semibold text-text">Jahr {label}</p>
			<p className="text-primary-600">
				{metric.label}: {metric.format(entry.value)}
				{isEstimate && <span className="text-caution-700"> (Schätzung)</span>}
			</p>
		</div>
	);
};

ChartTooltip.propTypes = {
	active: PropTypes.bool,
	payload: PropTypes.array,
	label: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
	metric: PropTypes.object,
	points: PropTypes.array,
};

const EstimateLegend = () => (
	<span className="flex items-center gap-1.5 text-[11px] font-medium text-bgBase-800">
		<span className="inline-block w-5 border-t-2 border-dashed border-primary-500" />
		Schätzung
	</span>
);

const CompanyFinancialChart = ({ financials }) => {
	const [activeMetricId, setActiveMetricId] = useState(METRICS[0].id);

	const metric = METRICS.find((m) => m.id === activeMetricId);
	const chartData = buildSplitData(financials, activeMetricId);
	const hasData = chartData.length >= 2;
	const hasEstimates = hasData && chartData.some((d) => d.isEstimate);

	const lineProps = {
		type: 'monotone',
		stroke: LINE_COLOR,
		strokeWidth: 2.5,
		dot: { fill: LINE_COLOR, r: 4 },
		activeDot: { r: 6 },
		connectNulls: false,
	};

	return (
		<SectionCard
			title="Finanzentwicklung"
			icon={BsGraphUp}
			bodyClassName="pt-0"
			headerAction={hasEstimates && <EstimateLegend />}
		>
			<Tabs tabs={METRICS} activeId={activeMetricId} onChange={setActiveMetricId} className="mb-4" />
			{hasData ? (
				<ResponsiveContainer width="100%" height={CHART_HEIGHT}>
					<LineChart data={chartData}>
						<CartesianGrid strokeDasharray="3 3" stroke={colors.bgBase[400]} />
						<XAxis dataKey="year" tick={AXIS_TICK} axisLine={AXIS_LINE} tickLine={AXIS_LINE} />
						<YAxis
							width={80}
							tick={AXIS_TICK}
							axisLine={AXIS_LINE}
							tickLine={AXIS_LINE}
							tickFormatter={(value) => metric.format(value)}
						/>
						<Tooltip content={<ChartTooltip metric={metric} points={chartData} />} />
						<Line {...lineProps} dataKey="confirmed" name={metric.label} />
						{hasEstimates && (
							<Line
								{...lineProps}
								dataKey="estimated"
								strokeDasharray="6 4"
								name={`${metric.label} (Schätzung)`}
								legendType="none"
							/>
						)}
					</LineChart>
				</ResponsiveContainer>
			) : (
				<div
					className="flex items-center justify-center text-sm text-bgBase-800"
					style={{ height: CHART_HEIGHT }}
				>
					Keine Daten für {metric.label} vorhanden
				</div>
			)}
		</SectionCard>
	);
};

CompanyFinancialChart.propTypes = {
	financials: PropTypes.array.isRequired,
};

export default CompanyFinancialChart;
